import { useEffect, useState } from 'react';
import SEO from '../components/SEO';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const HERO_IMAGES = [
  '/photo-site/lameijeete.jpg',
  '/photo-site/neije.png',
  '/photo-site/montagne.png',
];

const structuredData = {
  "@context": "https://schema.org",
  "@type": "LodgingBusiness",
  "name": "La Marmotte Joyeuse",
  "description": "Hébergement de charme dans les Alpes françaises à La Grave. Appartements confortables avec vue panoramique sur les montagnes.",
  "url": "https://marmottejoyeuse.fr",
  "telephone": "06 63 02 42 48",
  "email": "lamarmottejoyeuse@gmail.com",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "2 Impasse du Garde Champêtre",
    "addressLocality": "La Grave",
    "postalCode": "05320",
    "addressCountry": "FR"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "45.04473905830461",
    "longitude": "6.305305015731621"
  },
  "priceRange": "€€",
  "amenityFeature": [
    "WiFi gratuit",
    "Parking privé",
    "Vue sur les montagnes",
    "Cuisine équipée",
    "Chauffage"
  ],
  "image": "https://marmottejoyeuse.fr/photo-site/lameijeete.jpg"
};

const Home = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <SEO
        title={t('seo.home.title')}
        description={t('seo.home.description')}
        keywords="hébergement alpes, appartement la grave, location montagne, marmotte joyeuse, séjour alpes, station ski, randonnée montagne, hébergement la grave, location appartement alpes"
        structuredData={structuredData}
      />

      <div className="relative w-full h-screen overflow-hidden">
        {HERO_IMAGES.map((src, i) => (
          <img
            key={src}
            src={src}
            alt="La Marmotte Joyeuse – La Grave face à la Meije"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000
              ${i === currentSlide ? 'opacity-100' : 'opacity-0'}`}
            loading={i === 0 ? 'eager' : 'lazy'}
          />
        ))}

        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />

        <div className="absolute bottom-14 left-8 md:left-16 z-10 max-w-xl">
          <p className="text-white/60 text-xs tracking-widest uppercase mb-3">
            {t('hero.location')}
          </p>
          <h1 className="font-serif italic text-5xl md:text-7xl font-bold text-white leading-tight mb-6 drop-shadow-lg">
            La Marmotte<br />Joyeuse
          </h1>
          <p className="text-white/80 text-base md:text-lg mb-8">
            {t('hero.subtitle')}
          </p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/appartements')}
          >
            {t('hero.cta')}
          </button>
        </div>

        <div className="absolute bottom-8 left-8 md:left-16 flex gap-2 z-10">
          {HERO_IMAGES.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={`h-0.5 rounded-full transition-all duration-500
                ${i === currentSlide ? 'w-8 bg-white' : 'w-2 bg-white/40'}`}
              aria-label={`Photo ${i + 1}`}
            />
          ))}
        </div>

        <div className="absolute bottom-8 right-8 z-10 flex flex-col items-center gap-2">
          <span className="text-white/40 text-xs tracking-widest uppercase">{t('hero.scroll')}</span>
          <div className="w-px h-8 bg-white/30" />
        </div>
      </div>

      <section id="story" className="relative w-full h-[80vh] overflow-hidden">
        <img
          src="/photo-site/rando.jpg"
          alt="Randonnée en montagne – La Marmotte Joyeuse"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute right-0 top-0 h-full w-full md:w-[45%] flex items-center
                        bg-white/20 backdrop-blur-xl border-l border-white/30
                        px-8 md:px-12 py-12">
          <div>
            <span className="text-primary text-sm font-semibold tracking-wide uppercase">
              {t('story.label')}
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold mt-3 mb-6 leading-tight text-base-content">
              {t('story.title')}
            </h2>
            <p className="text-base-content/80 text-base leading-relaxed mb-4">
              {t('story.p1')} <strong>{t('story.minerals')}</strong>.
            </p>
            <p className="text-base-content/80 text-base leading-relaxed">
              {t('story.p2')}
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-24">
        <div className="bg-base-200 rounded-xl p-4">
          <h2 className="text-3xl font-bold mb-6 text-center">{t('location.title')}</h2>
          <div className="w-full h-[400px] rounded-lg overflow-hidden">
            <iframe
              title="Localisation La Marmotte Joyeuse"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2834.175167119725!2d6.305305015731621!3d45.04473905830461!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4789e446d3d7d0bb%3A0x7f7c77a5469f69!2s2%20Imp.%20du%20Garde%20Champ%C3%AAtre%2C%2005320%20La%20Grave!5e0!3m2!1sfr!2sfr!4v1690000000000!5m2!1sfr!2sfr"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <div className="mt-4 text-center">
            <p className="text-base-content/80">{t('location.address')}</p>
            <a
              href="https://maps.google.com/?q=2+Impasse+du+Garde+Champêtre,+05320+La+Grave,+France"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-2 text-primary hover:underline"
            >
              {t('location.openMaps')}
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;
